import React from "react";
import { BadgeCheck } from "lucide-react";

const EPDApprovalSection = ({
  data,
  onChange,
  lcaLockStatus = "unlocked",
  lcaVersionSubmitted = false,
}) => {
  const isEpdLocked = lcaLockStatus === "epd_locked";
  const canEdit =
    !isEpdLocked &&
    (lcaVersionSubmitted || lcaLockStatus === "calc_ended");
  const isDisabled = !canEdit;
  const isReadOnly = isEpdLocked;

  const fields = [
    {
      id: "declarationNumber",
      label: "Declaration number (EPD ID)",
      required: true,
      type: "text",
    },
    {
      id: "programmeOperator",
      label: "Programme operator (Publisher)",
      type: "text",
    },
    {
      id: "declarationOwner",
      label: "Declaration owner",
      type: "text",
    },
    { id: "issueDate", label: "Issue date", type: "date" },
    { id: "validTo", label: "Valid to", type: "date" },
    {
      id: "verifiedBy",
      label: "Verified by (organisation / person)",
      type: "text",
    },
    { id: "verificationDate", label: "Verification date", type: "date" },
  ];

  const PLACEHOLDER = "Available after LCA submission";

  return (
    <div className="rounded-xl border-l-4 border-l-emerald-500/60 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <BadgeCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          EPD Approval (Third-party verified)
        </h3>
        {isEpdLocked ? (
          <span className="ml-auto text-[10px] px-2 py-0.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-400/40 rounded font-semibold">
            Verified
          </span>
        ) : canEdit ? (
          <span className="ml-auto text-[10px] px-2 py-0.5 bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-400/40 rounded font-semibold">
            Ready to complete
          </span>
        ) : (
          <span className="ml-auto text-[10px] px-2 py-0.5 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded">
            Pending verification
          </span>
        )}
      </div>

      {isDisabled && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 italic">
          These fields become editable once the LCA version is{" "}
          <strong>submitted for verification</strong>, or when calculation is{" "}
          <strong>marked finished</strong>.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map(({ id, label, type = "text", required }) => (
          <div
            key={id}
            className={id === "declarationNumber" ? "md:col-span-2" : ""}
          >
            <label
              htmlFor={`epd-${id}`}
              className="text-xs font-medium mb-1.5 block text-gray-700 dark:text-gray-300"
            >
              {label}
              {required && canEdit && (
                <span className="text-red-500 ml-0.5">*</span>
              )}
            </label>
            <input
              id={`epd-${id}`}
              type={type}
              value={data[id] || ""}
              onChange={(e) => onChange(id, e.target.value)}
              disabled={isDisabled}
              readOnly={isReadOnly}
              placeholder={isDisabled ? PLACEHOLDER : ""}
              className={`h-9 text-sm w-full px-3 rounded-md border transition-all ${
                isDisabled
                  ? "bg-gray-100 dark:bg-gray-800/40 text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60"
                  : isReadOnly
                  ? "bg-gray-50 dark:bg-gray-800/20 border-gray-200 dark:border-gray-700 cursor-default"
                  : "bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              }`}
            />
          </div>
        ))}
      </div>

      {canEdit && !isEpdLocked && (
        <p className="text-[10px] text-sky-600 dark:text-sky-400 mt-3 flex items-center gap-1">
          <BadgeCheck className="h-3 w-3" />
          Complete verifier details before locking EPD and publishing the DPP.
        </p>
      )}
      {isReadOnly && (
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-3 italic">
          Read-only — EPD approval data is locked for audit integrity.
        </p>
      )}
    </div>
  );
};

export default EPDApprovalSection;
